import { useEffect, useState } from 'react';
import { Spinner, Stack } from 'react-bootstrap';
import { FaArrowLeft } from 'react-icons/fa6';
import { To, useNavigate, useParams } from 'react-router-dom';
import { SimpleButton } from '../../../../../components/buttons/SimpleButton';
import { DetailSection, LabelText } from '../../../../../components/generic';
import ConfirmationModal from '../../../../../components/modals/ConfirmationModal';
import { useToast } from '../../../../../context/ToastContext';
import {
  useDeleteThemeMutation,
  useGetThemeQuery
} from '../../../../../services/endpoints/content/theme';
import { getCurrentUser } from '../../../../../services/helper';
import { Theme } from '../../../../../types/theme.type';
import { canEditThemes } from '../../../../../utils/rbac';
import { getErrorMessage } from '../../../../../utils/utils';
import SafeHTMLDisplay from '../../../../common/SafeHTMLDisplay';

export const ThemeDetails = () => {
  const params = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { data, error, isLoading } = useGetThemeQuery(+params.id!);
  const [deleteTheme, { isLoading: isDeleting }] = useDeleteThemeMutation();
  const [theme, setTheme] = useState<Theme>();
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (error && !isLoading) {
      addToast('Error while loading theme', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, error, isLoading]);

  useEffect(() => {
    if (data && data.theme) {
      setTheme(data.theme);
    }
  }, [data]);

  const handleDelete = async () => {
    try {
      await deleteTheme(+params.id!).unwrap();
      addToast('Theme deleted successfully', 'success');
      navigate('/admin/content/themes');
    } catch (e) {
      addToast(getErrorMessage(e), 'error');
    }
  };

  if (isLoading || !theme) {
    return (
      <div className="w-100 vh-100 d-inline-flex">
        <Spinner
          variant="primary"
          className="m-auto"
        />
      </div>
    );
  }

  return (
    <>
      <div
        className="d-flex justify-content-between align-items-center px-lg-5 border-bottom px-4 py-3"
        style={{ background: '#EFEFEF' }}
      >
        <Stack
          direction="horizontal"
          gap={2}
          className="ps-lg-3 ps-0"
        >
          <button
            className="btn btn-link text-info p-0"
            onClick={() => navigate(-1 as To)}
          >
            <FaArrowLeft
              size={20}
              className="d-lg-none"
            />
            <FaArrowLeft
              size={24}
              className="d-none d-lg-block"
            />
          </button>
          <h2
            className="d-lg-none text-secondary fw-semibold text-uppercase mb-0 pt-1"
            style={{ color: '#888888', fontSize: '1rem' }}
          >
            Themes List
          </h2>
          <h2 className="d-none d-lg-block text-info fw-bold mb-0">{`Theme #${theme?.id}`}</h2>
        </Stack>
        {canEditThemes(getCurrentUser()) && (
          <Stack
            direction="horizontal"
            gap={1}
            className="d-none d-lg-flex"
          >
            <SimpleButton
              variant="primary"
              height={36}
              onClick={() => navigate(`/admin/content/themes/${theme?.id}/edit`)}
            >
              Edit Theme
            </SimpleButton>
            <SimpleButton
              variant="primary"
              height={36}
              onClick={() => setShowModal(true)}
            >
              Delete Theme
            </SimpleButton>
          </Stack>
        )}
      </div>
      <div>
        <Stack
          gap={4}
          className="p-4"
        >
          <DetailSection>
            <LabelText
              label="Id:"
              text={theme?.id}
            />
            <LabelText
              label="Name:"
              text={theme?.name}
            />
            <LabelText label="Details:">
              <SafeHTMLDisplay htmlContent={theme?.details} />
            </LabelText>
          </DetailSection>
        </Stack>
      </div>

      {canEditThemes(getCurrentUser()) && (
        <ConfirmationModal
          show={showModal}
          title="Delete Theme Plan"
          message="Are you sure you want to remove this theme?"
          confirmText="Yes, Delete"
          cancelText="Cancel"
          onConfirm={handleDelete}
          onCancel={() => setShowModal(false)}
          isLoading={isDeleting}
        />
      )}
    </>
  );
};
